import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  FormAnswer,
  Task,
  TaskCompletionFilterInputObject,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import useArrowKeyNavigation from '@lib/hooks/useArrowKeyNavigation';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Table from '@lib/components/Table/Table';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Typography from '@lib/components/Typography/Typography';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import AssetChip from '@lib/components/AssetChip/AssetChip';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import { getValidityHintChipIcon } from '@lib/utils/validityHint';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import buildTaskCompletionDetailsRows from '@lib/components/TaskCompletions/utils/buildTaskCompletionDetailsRows';
import getTaskCompletionDetailsTableColumns from '@lib/components/TaskCompletions/utils/getTaskCompletionDetailsTableColumns';
import getTableRowClassNameByValidityHint from '@lib/components/TaskCompletions/utils/getTableRowClassNameByValidityHint';
import { TaskCompletionDetailsTableRow } from '@lib/components/TaskCompletions/types';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggle from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
import TaskCompletionsCompareTable from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/TaskCompletionsCompareTable';
import CompletionStatusFilter from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/CompletionStatusFilter';
import getCompareDateRangeLabel from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/utils/getCompareDateRangeLabel';
import getWorstValidityHint from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/utils/getWorstValidityHint';
import getFullName from '@lib/utils/getFullName';
import modalStyles from '../TaskCompletionsModal.module.scss';
import useDocumentationCellAccess from '../utils/useDocumentationCellAccess';
import useTaskCompletionDetailsViewActions from './utils/useTaskCompletionDetailsViewActions';
import useCompareCompletionsData from './utils/useCompareCompletionsData';
import styles from './TaskCompletionDetails.module.scss';
import { TaskCompletionsModalUrls } from '../types';

type Props = {
  task: Task | null | undefined;
  groupKey: string | null | undefined;
  completionId?: string | null;
  onChangeCompletionId: (id: string) => void;
  orderingField?: string | null;
  orderingDirection?: string | null;
  bulkCompareIds?: string[] | null;
  onBackToList?: () => void;
  // Narrows which completions prev/next browse (e.g. a timeline bucket's
  // period) when there's no `fetchIds` override.
  baseFilters?: TaskCompletionFilterInputObject[];
  // Overrides which completions prev/next and compare mode browse — see
  // `fetchIdsOverride` in useTaskCompletionDetailsViewActions.
  fetchIds?: () => Promise<FetchIdsResponse<unknown>>;
  showTaskChip?: boolean;
  urls: TaskCompletionsModalUrls;
};

export default function TaskCompletionDetails(props: Props) {
  const {
    task,
    groupKey,
    completionId,
    onChangeCompletionId,
    orderingField,
    orderingDirection,
    bulkCompareIds,
    onBackToList,
    baseFilters,
    fetchIds,
    showTaskChip,
    urls,
  } = props;
  const { t } = useTranslation();
  const { isViewLocked, isFullAdmin, onUpgradePlan, onDocumentClick } =
    useDocumentationCellAccess(urls.getDocumentationUrl);

  const {
    statusFilter,
    setStatusFilter,
    allIds,
    allIdsLoading,
    currentIndex,
    isEmpty,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
    completionData,
    completionLoading,
    completionFirstLoading,
    completionError,
    isBulkCompare,
    isCompareMode,
    onToggleCompareMode,
    comparePageIds,
    compareTotalCount,
    compareAnchorIndex,
    onCompareNavigatePrev,
    onCompareNavigateNext,
    canCompareNavigatePrev,
    canCompareNavigateNext,
  } = useTaskCompletionDetailsViewActions({
    groupKey,
    completionId,
    onChangeCompletionId,
    isViewLocked,
    orderingField,
    orderingDirection,
    bulkCompareIds,
    baseFilters,
    fetchIdsOverride: fetchIds,
  });

  // Compare mode pages through the compared set; single mode steps through
  // siblings one at a time — arrow keys drive whichever is active.
  const onArrowPrev = isCompareMode ? onCompareNavigatePrev : onNavigatePrev;
  const onArrowNext = isCompareMode ? onCompareNavigateNext : onNavigateNext;
  const canArrowPrev = isCompareMode
    ? canCompareNavigatePrev
    : canNavigatePrev && !allIdsLoading;
  const canArrowNext = isCompareMode
    ? canCompareNavigateNext
    : canNavigateNext && !allIdsLoading;

  useArrowKeyNavigation([
    { keys: 'ArrowLeft', handler: onArrowPrev, disabled: !canArrowPrev },
    { keys: 'ArrowRight', handler: onArrowNext, disabled: !canArrowNext },
  ]);

  // Only fetch compared completions while compare mode is actually on —
  // this hook is called unconditionally (rules of hooks), but its per-slot
  // queries are individually skippable, so an empty id list keeps it inert
  // in single-completion mode instead of firing 3 unneeded queries.
  const {
    columns: compareColumns,
    rows: compareRows,
    firstLoading: compareFirstLoading,
    error: compareError,
  } = useCompareCompletionsData(
    isCompareMode ? comparePageIds : [],
    isViewLocked,
  );

  const compareDateRangeLabel = useMemo(
    () =>
      getCompareDateRangeLabel(
        compareColumns.map((column) => column.completion?.createdAt),
      ),
    [compareColumns],
  );
  const compareWorstValidityHint = useMemo(
    () =>
      getWorstValidityHint(
        compareColumns.map((column) => column.completion?.validityHint),
      ),
    [compareColumns],
  );

  const completionTask = completionData?.task;
  const performer = completionData?.performer;
  const version = completionTask?.formTemplateVersion?.version
    ? `v${completionTask.formTemplateVersion.version}`
    : undefined;

  const answers = completionData?.answers as FormAnswer[];
  const questions = completionTask?.formTemplateVersion?.questions;

  const allTableData = useMemo<TaskCompletionDetailsTableRow[]>(
    () => buildTaskCompletionDetailsRows(questions, answers),
    [answers, questions],
  );

  // Single-completion view: each row is one question with its own answer,
  // so the status filter can check `answer.validityHint` directly — same
  // field the leading status-icon column already renders per row.
  const tableData = useMemo(() => {
    if (!statusFilter.length) return allTableData;
    const allowedHints = new Set(
      statusFilter.map(({ value }) => String(value)),
    );
    return allTableData.filter(
      (row) =>
        row.answer?.validityHint && allowedHints.has(row.answer.validityHint),
    );
  }, [allTableData, statusFilter]);

  const readingDurationByDocumentationId = useMemo(() => {
    const map = new Map<string, number>();
    (answers || []).forEach((answer) => {
      (answer?.documentationVersionViews || []).forEach((view) => {
        if (
          view?.documentationId &&
          typeof view.readingDurationSeconds === 'number' &&
          !map.has(view.documentationId)
        ) {
          map.set(view.documentationId, view.readingDurationSeconds);
        }
      });
    });
    return map;
  }, [answers]);

  const columns = useMemo(
    () =>
      getTaskCompletionDetailsTableColumns({
        t,
        readingDurationByDocumentationId,
        onDocumentClick,
        isViewLocked,
        isFullAdmin,
        onUpgradePlan,
      }),
    [
      t,
      readingDurationByDocumentationId,
      onDocumentClick,
      isViewLocked,
      isFullAdmin,
      onUpgradePlan,
    ],
  );

  const emptyState = (
    <ListEmptyState
      isFilterApplied={!!statusFilter.length}
      onResetFilter={() => setStatusFilter([])}
      title={t('no-results-found')}
      text={t('no-results-found-text')}
    />
  );

  let navCounterNode: React.ReactNode = null;
  if (isCompareMode) {
    if (compareTotalCount > 0) {
      navCounterNode = (
        <Typography variant="body">
          {t('range-of-total', {
            from: compareAnchorIndex + 1,
            to: Math.min(
              compareAnchorIndex + comparePageIds.length,
              compareTotalCount,
            ),
            total: compareTotalCount,
          })}
        </Typography>
      );
    }
  } else if (allIdsLoading) {
    navCounterNode = <CircleLoader size={16} padding={0} />;
  } else if (allIds.length > 0) {
    navCounterNode = (
      <Typography variant="body">
        {t('count-of-total', {
          count: currentIndex + 1,
          total: allIds.length,
        })}
      </Typography>
    );
  }

  const headerValidityHint = isCompareMode
    ? compareWorstValidityHint
    : completionData?.validityHint;
  const headerDateLabel = isCompareMode
    ? compareDateRangeLabel
    : completionData?.createdAt &&
      getFormattedDate(completionData.createdAt, dateTimeFormatEU);

  const headerRow = !isEmpty && (
    <div className={modalStyles.headerRow}>
      <div className={modalStyles.headerLeftGroup}>
        <div
          className={classNames(
            modalStyles.headerItem,
            modalStyles.headerItemDate,
          )}
        >
          {getValidityHintChipIcon(headerValidityHint)}
          <Typography variant="body">{headerDateLabel}</Typography>
        </div>
        <div className={modalStyles.headerDivider} />
        <div className={modalStyles.headerChips}>
          {showTaskChip && task?.name && (
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.small}
              leftIcon="task_alt"
              leftIconSymbolsOutlined
              label={task.name}
            />
          )}
          {task?.site?.name && (
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.small}
              leftIcon="home_pin"
              leftIconSymbolsOutlined
              label={task.site.name}
            />
          )}
          {task?.siteArea?.name && (
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.small}
              leftIcon="location_on"
              leftIconSymbolsOutlined
              label={task.siteArea.name}
            />
          )}
          {task?.asset && (
            <AssetChip asset={task.asset} size={ChipSizes.small} />
          )}
        </div>
      </div>
      {/* Compare mode shows each completion's performer in its own table
          column header instead — a single performer here wouldn't apply to
          the whole comparison. */}
      {!isCompareMode && performer && (
        <>
          <div className={modalStyles.headerDivider} />
          <div className={modalStyles.headerItem}>
            <UserPreviewCell
              name={getFullName(performer)}
              avatar={performer.avatarAttached?.urls?.small}
            />
          </div>
        </>
      )}
    </div>
  );

  let bodyContent: React.ReactNode;
  if (isEmpty) {
    bodyContent = emptyState;
  } else if (isCompareMode) {
    // `comparePageIds` is sliced from `allIds`, which is still `[]` while the
    // sibling-ids fetch is in flight — rendering the compare table with an
    // empty id list makes `useCompareCompletionsData` skip every query slot,
    // so it reports `firstLoading: false` and the table shows its own empty
    // state instead of a spinner. Defer to the ids fetch's own loading flag.
    bodyContent = allIdsLoading ? (
      <CircleLoader />
    ) : (
      <TaskCompletionsCompareTable
        columns={compareColumns}
        rows={compareRows}
        firstLoading={compareFirstLoading}
        error={compareError}
        statusFilter={statusFilter}
        isViewLocked={isViewLocked}
        isFullAdmin={isFullAdmin}
        onUpgradePlan={onUpgradePlan}
        onDocumentClick={onDocumentClick}
      />
    );
  } else {
    bodyContent = (
      <div className={styles.tableWrapper}>
        {completionError && (
          <ErrorPage
            description={getDescriptionFromError(completionError)}
            status={500}
          />
        )}
        {completionFirstLoading && <CircleLoader />}
        {!completionError && !completionFirstLoading && (
          <Table<TaskCompletionDetailsTableRow>
            id="task-completion-details"
            columns={columns}
            data={tableData}
            totalEntries={tableData.length}
            loading={completionLoading}
            getRowClassName={(item) =>
              getTableRowClassNameByValidityHint(item.answer?.validityHint)
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.statusFilter}>
            <CompletionStatusFilter
              t={t}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </div>
          {!!statusFilter.length && (
            <div>
              <Button
                buttonType={ButtonTypes.primaryText}
                buttonText={t('reset')}
                onClick={() => setStatusFilter([])}
              />
            </div>
          )}
          {(isBulkCompare || allIds.length > 1) && (
            <div>
              <CheckBoxSingleBool
                name="task-completion-compare-mode"
                value={isCompareMode}
                options={[{ label: 'checked', value: 'checked' }]}
                onChange={(value) =>
                  isBulkCompare ? onBackToList?.() : onToggleCompareMode(value)
                }
                label={t('compare-mode')}
                CheckBoxComponent={CheckBoxAsToggle}
                labelTypographyProps={{ variant: 'body', strong: false }}
                size={CheckboxSizes.Small}
              />
            </div>
          )}
        </div>
        <div className={styles.toolbarRight}>
          {navCounterNode}
          {version && <Chip label={version} type={ChipTypes.chipBlack} />}
          <div className={styles.nav}>
            <IconButton
              icon="arrow_back"
              type={IconButtonTypes.PrimaryOutlined}
              disabled={!canArrowPrev}
              onClick={onArrowPrev}
            />
            <IconButton
              icon="arrow_forward"
              type={IconButtonTypes.PrimaryOutlined}
              disabled={!canArrowNext}
              onClick={onArrowNext}
            />
          </div>
        </div>
      </div>
      {headerRow}
      {bodyContent}
    </div>
  );
}
